// src/utils/adherenceUtils.js
import { db } from "../firebase";
import {
  collection, getDocs, query, where, orderBy, limit, Timestamp
} from "firebase/firestore";

/**
 * Computes adherence over a lookback window using the exact same logic
 * for every screen (dashboard & patient).
 *
 * 1) Prefer patients/<id>/daily_summaries (within lookbackDays)
 * 2) Fallback to patients/<id>/daily_med_status (recent rows)
 * Returns { risk: "low"|"medium"|"high"|"" , pct, taken, total }
 */
export async function computeRiskFromAdherence(clinicCode, patientId, lookbackDays = 30) {
  try {
    const base = ["clinics", clinicCode, "patients", patientId, "daily_summaries"];
    const sinceISO = new Date(Date.now() - lookbackDays * 864e5).toISOString().slice(0, 10);
    const sinceStart = Timestamp.fromDate(new Date(`${sinceISO}T00:00:00.000Z`));

    // Prefer daily_summaries window
    const sumSnap = await getDocs(
      query(collection(db, ...base), where("date", ">=", sinceStart), orderBy("date", "desc"))
    );

    let taken = 0, total = 0;

    if (!sumSnap.empty) {
      sumSnap.forEach((d) => {
        const v = d.data() || {};
        const t = Number(v.taken || v.takenCount || 0);
        const sched = Number(v.totalScheduled || v.total || v.scheduled || v.totalCount || v.count || 0);
        if (sched > 0) { taken += t; total += sched; }
      });
    } else {
      // Fallback: recent daily_med_status rows
      const statRef = collection(db, "clinics", clinicCode, "patients", patientId, "daily_med_status");
      const statSnap = await getDocs(query(statRef, orderBy("date", "desc"), limit(14)));
      statSnap.forEach((d) => {
        const v = d.data() || {};
        if (v.taken === true || (typeof v.status === "string" && v.status.toLowerCase() === "taken")) {
          taken += 1; total += 1;
        } else if (v.taken === false || (typeof v.status === "string" && v.status.toLowerCase() === "missed")) {
          total += 1;
        } else if (typeof v.total === "number" && typeof v.takenCount === "number") {
          taken += Number(v.takenCount); total += Number(v.total);
        }
      });
    }

    if (total <= 0) return { risk: "", pct: null, taken: 0, total: 0 };
    const pct = Math.round((taken / total) * 100);
    const risk = pct >= 80 ? "low" : pct >= 50 ? "medium" : "high";
    return { risk, pct, taken, total };
  } catch (e) {
    console.warn("Adherence compute failed", e);
    return { risk: "", pct: null, taken: 0, total: 0 };
  }
}
